import { useCallback, useMemo } from 'react';
import { useRenderingInfo } from '~/contexts/useRenderingInfo';
import { QuerySet_State } from '~/core/state/types/state-sets.type';

interface UseShownAttributesProps {
    querySetState: QuerySet_State;
}

export type StripNotRenderedAttributesFun = <T extends Record<string, any>, K extends keyof T>(source: T) => Pick<T, K>;

interface UseShownAttributesResult {
    shownAttributes: string[];
    shouldRenderKey: (key: string) => boolean;
    stripNotRenderedAttributes: StripNotRenderedAttributesFun;
}

export const useShownAttributes = ({ querySetState }: UseShownAttributesProps): UseShownAttributesResult => {
    const { isMobileVersion } = useRenderingInfo();

    const shownAttributes = useMemo(
        () =>
            (isMobileVersion
                ? querySetState.parameters?.visibleAttributesMobile
                : querySetState.parameters?.visibleAttributesDesktop) ?? [],
        [isMobileVersion, querySetState]
    );

    const shouldRenderKey = useCallback(
        (key: string) => {
            const allwaysRenderedAttributes = ['url', 'title'];
            return (
                allwaysRenderedAttributes.includes(key) ||
                shownAttributes.length === 0 ||
                shownAttributes.findIndex((value) => value.toLowerCase() === key.toLowerCase()) !== -1
            );
        },
        [shownAttributes]
    );

    const stripNotRenderedAttributes: StripNotRenderedAttributesFun = useCallback(
        <T extends Record<string, any>, K extends keyof T>(source: T) => {
            return Object.fromEntries(Object.entries(source).filter(([key]) => shouldRenderKey(key))) as Pick<T, K>;
        },
        [shouldRenderKey]
    );

    return {
        shownAttributes: shownAttributes,
        shouldRenderKey,
        stripNotRenderedAttributes,
    };
};
